import { useState, useCallback } from 'react';
import { useToast } from './useToast';
import { useLockJobMutation } from '../store/slices/apiSlice';
import type { JobLockData } from '../utils/types';
import { isJobLocked } from '../utils/lockHelpers';

interface UseJobLockProps {
  jobId: string;
  currentLock: JobLockData | null | undefined;
  onLockUpdate?: (lockData: JobLockData) => void;
}

interface UseJobLockReturn {
  isLocked: boolean;
  lockData: JobLockData | null | undefined;
  isLoading: boolean;
  lockJob: (reason: string) => Promise<void>;
  unlockJob: () => Promise<void>;
  canModifyDates: boolean;
  showLockError: () => void;
}

/**
 * Hook for managing job lock state and operations
 * 
 * Central hook for lock functionality - use this everywhere lock is needed.
 * Provides lock/unlock operations and validation helpers.
 * 
 * @example
 * ```typescript
 * const { isLocked, canModifyDates, lockJob, unlockJob, showLockError } = useJobLock({
 *   jobId: job.id,
 *   currentLock: job.lock_data
 * });
 * 
 * // Check before modifying dates
 * if (!canModifyDates) {
 *   showLockError();
 *   return;
 * }
 * ```
 */
export const useJobLock = ({
  jobId,
  currentLock,
  onLockUpdate
}: UseJobLockProps): UseJobLockReturn => {
  const toast = useToast();
  const [lockJobMutation] = useLockJobMutation();
  const [isLoading, setIsLoading] = useState(false);

  const locked = isJobLocked(currentLock);
  const canModify = !locked;

  /**
   * Lock the job with a reason
   */
  const lockJob = useCallback(async (reason: string) => {
    if (!reason.trim()) {
      toast.error('Lock Error', 'Please provide a reason for locking this job');
      return;
    }

    setIsLoading(true);
    try {
      const response = await lockJobMutation({
        id: jobId,
        is_locked: true,
        reason: reason.trim()
      }).unwrap();

      toast.success('Job Locked', 'Job has been locked successfully');
      
      // Notify parent
      if (onLockUpdate && response.lock_data) {
        onLockUpdate(response.lock_data);
      }
    } catch (error: any) {
      toast.error(
        'Lock Failed',
        error?.data?.detail || 'Failed to lock job. Please try again.'
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [jobId, lockJobMutation, toast, onLockUpdate]);

  /**
   * Unlock the job
   */
  const unlockJob = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await lockJobMutation({
        id: jobId,
        is_locked: false
      }).unwrap();

      toast.success('Job Unlocked', 'Job has been unlocked successfully');
      
      // Notify parent
      if (onLockUpdate && response.lock_data) {
        onLockUpdate(response.lock_data);
      }
    } catch (error: any) {
      toast.error(
        'Unlock Failed',
        error?.data?.detail || 'Failed to unlock job. Please try again.'
      );
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [jobId, lockJobMutation, toast, onLockUpdate]);

  /**
   * Show lock error message
   * Call this when user tries to modify a locked job
   */
  const showLockError = useCallback(() => {
    const reason = currentLock?.reason || 'No reason provided';
    const lockedBy = currentLock?.locked_by || 'Unknown';
    
    toast.error(
      'Job is Locked',
      `Cannot modify dates or reschedule.\n\nReason: ${reason}\nLocked by: ${lockedBy}\n\nPlease unlock the job first.`,
      { duration: 6000 }
    );
  }, [currentLock, toast]);

  return {
    isLocked: locked,
    lockData: currentLock,
    isLoading,
    lockJob,
    unlockJob,
    canModifyDates: canModify,
    showLockError
  };
};



